import { useAuth } from '@/hooks/useAuth';
import { useFamily } from '@/hooks/useFamily';

export interface LoadingStates {
  isAuthLoading: boolean;
  isFamilyLoading: boolean;
  isFullyLoaded: boolean;
  hasFamily: boolean;
  hasUser: boolean;
  shouldShowSkeleton: boolean;
  shouldShowNoFamily: boolean;
}

/**
 * Centralized hook for managing loading states across the app
 * Prevents showing "create family" messages during loading
 */
export const useLoadingStates = (): LoadingStates => {
  const { user, loading: authLoading } = useAuth();
  const { family, loading: familyLoading } = useFamily();

  const isAuthLoading = authLoading;
  const isFamilyLoading = familyLoading;
  const hasUser = !!user;
  const hasFamily = !!family;

  // We're fully loaded when auth is done AND (no user OR family loading is done)
  const isFullyLoaded = !authLoading && (!user || !familyLoading);
  
  // Show skeleton when auth is loading OR when user exists but family is still loading
  const shouldShowSkeleton = authLoading || (user && familyLoading);
  
  // Only show "no family" message when fully loaded, has user, but no family
  const shouldShowNoFamily = isFullyLoaded && hasUser && !hasFamily;

  return {
    isAuthLoading,
    isFamilyLoading,
    isFullyLoaded,
    hasFamily,
    hasUser,
    shouldShowSkeleton,
    shouldShowNoFamily,
  };
};